import React from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import { formatNisAmount, formatPercentUsed } from '../../utils/labData';

/**
 * Lab Data budget burn metrics table (row click opens detail).
 */
const BudgetLinesTable = ({ rows = [], onEdit, dateFrom, dateTo }) => {
  const navigate = useNavigate();

  const openDetail = row => {
    const params = new URLSearchParams();
    if (dateFrom) params.set('from', dateFrom);
    if (dateTo) params.set('to', dateTo);
    const qs = params.toString();
    navigate(`/lab-data/budgets/${row.id}${qs ? `?${qs}` : ''}`);
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Budget burn
        </Typography>
        {!rows?.length ? (
          <Typography color="text.secondary">No budget burn data yet.</Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Number</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>PI</TableCell>
                <TableCell align="right">Allocated</TableCell>
                <TableCell align="right">Spent</TableCell>
                <TableCell align="right">Remaining</TableCell>
                <TableCell width={160}>% used</TableCell>
                <TableCell align="right" width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => {
                const pct = Number(row.percent_used ?? 0);
                const clamped = Math.min(100, Math.max(0, pct));
                const numberLabel = row.number || row.code || '—';
                const pi = row.primary_investigator;
                const piLabel = pi
                  ? `${pi.first_name || ''} ${pi.last_name || ''}`.trim() ||
                    pi.username ||
                    '—'
                  : '—';
                return (
                  <TableRow
                    key={row.id}
                    hover
                    className="lab-data-row-clickable"
                    onClick={() => openDetail(row)}
                  >
                    <TableCell>{numberLabel}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{piLabel}</TableCell>
                    <TableCell align="right">
                      {formatNisAmount(row.allocated_amount)} {row.currency || ''}
                    </TableCell>
                    <TableCell align="right">{formatNisAmount(row.spent_nis)}</TableCell>
                    <TableCell align="right">{formatNisAmount(row.remaining_nis)}</TableCell>
                    <TableCell>
                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        {formatPercentUsed(pct)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={clamped}
                        color={clamped >= 90 ? 'error' : clamped >= 70 ? 'warning' : 'primary'}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={e => e.stopPropagation()}>
                      {onEdit ? (
                        <IconButton size="small" aria-label="Edit" onClick={() => onEdit(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default BudgetLinesTable;
